<template>
  <LcModal
    :background-class="leaving ? 'review-plays-modal-leaving' : undefined"
    :close-triggers="['click-outside', 'escape']"
    container-class="review-plays-modal-wrap"
    :tracking="{
      action: 'checkout',
      customMessage: message,
      customTopic: headline,
      topicKey: 'review_plays',
    }"
    @close="leave('close')"
  >
    <template #modal="{ setRef }">
      <!-- below a spotlit play, which stays on the page lifted above the backdrop -->
      <div
        ref="root"
        :class="['review-plays-modal w-full', { 'review-plays-modal-below-play': belowPlay }]"
        :style="belowPlay ?? undefined"
      >
        <LcCard
          :ref="setRef"
          body-class="lc-modal__body gap-200"
          class="lc-modal__card lc-modal__card--medium"
          :aria-labelledby="headlineId"
          aria-modal="true"
          role="dialog"
        >
          <header class="flex items-center justify-between gap-100 w-full">
            <h2 :id="headlineId" class="headline-h4-headline text-left">{{ headline }}</h2>
            <LcButton icon="close" size="small" variant="tertiary" @click="leave('close')" />
          </header>

          <p class="paragraph-regular text-left w-full text-secondary-secondary">{{ message }}</p>

          <footer class="flex flex-col gap-200 w-full">
            <LcButton variant="filled" @click="leave('edit')">
              {{ $t('editor.reviewplays.modal.edit') }}
            </LcButton>
            <LcButton
              :count="plays.length > 1 ? plays.length : undefined"
              @click="leave('quick-pick')"
            >
              {{ $t('editor.reviewplays.modal.quickpick') }}
            </LcButton>
          </footer>
        </LcCard>
      </div>
    </template>
  </LcModal>
</template>

<script lang="ts" setup>
import { EDITOR_REVIEW_MODAL_FADE_MS, EDITOR_REVIEW_MODAL_GAP_PX } from '../constants/editor'
import { toPlaySelector } from '../utils/editorPlay'

type Event = 'close' | 'edit' | 'quick-pick'

type Emits = {
  (event: Event): void
}

type Props = {
  plays: { id: string }[]
}

const emit = defineEmits<Emits>()
const { plays } = defineProps<Props>()

const { t } = useI18n()
const spotlight = useEditorSpotlight()

const headlineId = useId()
const headline = computed(() => t('editor.reviewplays.modal.headline', plays.length))
const message = computed(() => t('editor.reviewplays.modal.message', plays.length))
const belowPlay = ref<Maybe<{ left: string; top: string; width: string }>>(null)
const leaving = ref(false)
const root = useTemplateRef<HTMLElement>('root')

// the modal manager unmounts at once, so the backdrop fades out first, the spotlit play still above it
const leave = (event: Event) => {
  if (leaving.value) return
  leaving.value = true
  setTimeout(() => emit(event), EDITOR_REVIEW_MODAL_FADE_MS)
}

const placeBelowSpotlight = () => {
  const play = spotlight.value && document.querySelector(toPlaySelector(spotlight.value))
  if (!play) {
    belowPlay.value = null
    return
  }

  // relative to the wrap, which slides in, so the modal slides along and stops right below the play;
  // its offsets leave the slide transform out
  const wrap = root.value?.parentElement
  const wrapLeft = wrap?.offsetLeft ?? 0
  const wrapTop = (wrap?.offsetTop ?? 0) - (wrap?.offsetParent?.scrollTop ?? 0)

  const { bottom, left, width } = play.getBoundingClientRect()
  belowPlay.value = {
    left: `${left - wrapLeft}px`,
    top: `${bottom + EDITOR_REVIEW_MODAL_GAP_PX - wrapTop}px`,
    width: `${width}px`,
  }
}

// the modal content renders a tick after mount, once its transition starts
watch(root, placeBelowSpotlight)
useEventListener(window, 'resize', placeBelowSpotlight)
</script>

<style lang="scss" scoped>
.review-plays-modal {
  max-width: 48rem;
}

:global(.review-plays-modal-wrap) {
  position: relative;
}

:global(.review-plays-modal-leaving) {
  opacity: 0;
  transition: opacity $transition-duration-md ease;
}

// positioned against the fixed backdrop, which scrolls should the modal not fit below the play
.review-plays-modal-below-play {
  position: absolute;
}
</style>
